"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useSyncExternalStore } from "react";
export type FormFieldErrors = Record<string, string>;
type FormValidationEntry = {
  errors: FormFieldErrors;
  version: number;
};

/** Stable empty map for `useSyncExternalStore` — a fresh `{}` each call causes React #185. */
const EMPTY_FORM_FIELD_ERRORS: FormFieldErrors = Object.freeze({});
const formValidationState = new Map<string, FormValidationEntry>();
const listeners = new Map<string, Set<() => void>>();
function notify(formInstanceId: string): void {
  for (const listener of listeners.get(formInstanceId) ?? []) {
    listener();
  }
}
export function getFormFieldErrorsSnapshot(formInstanceId: string): FormFieldErrors {
  return formValidationState.get(formInstanceId)?.errors ?? EMPTY_FORM_FIELD_ERRORS;
}
export function setFormFieldErrorsForInstance(formInstanceId: string, errors: FormFieldErrors): void {
  const prev = formValidationState.get(formInstanceId);
  const nextErrors = Object.keys(errors).length === 0 ? EMPTY_FORM_FIELD_ERRORS : errors;
  formValidationState.set(formInstanceId, {
    errors: nextErrors,
    version: (prev?.version ?? 0) + 1
  });
  notify(formInstanceId);
}
export function clearFormFieldErrorsForInstance(formInstanceId: string): void {
  setFormFieldErrorsForInstance(formInstanceId, EMPTY_FORM_FIELD_ERRORS);
}
export function clearFormFieldErrorForInstance(formInstanceId: string, fieldName: string): void {
  const entry = formValidationState.get(formInstanceId);
  if (!entry?.errors[fieldName]) {
    return;
  }
  const next = {
    ...entry.errors
  };
  delete next[fieldName];
  setFormFieldErrorsForInstance(formInstanceId, next);
}
function subscribeToFormInstance(formInstanceId: string, onStoreChange: () => void): () => void {
  let set = listeners.get(formInstanceId);
  if (!set) {
    set = new Set();
    listeners.set(formInstanceId, set);
  }
  set.add(onStoreChange);
  return () => {
    set?.delete(onStoreChange);
    if (set?.size === 0) {
      listeners.delete(formInstanceId);
    }
  };
}

/** Subscribe to per-field errors for a public form instance (works across RSC boundaries). */
export function useFormFieldErrorsMap(formInstanceId) {
  const $ = _c(4);
  let t0;
  if ($[0] !== formInstanceId) {
    t0 = onStoreChange => formInstanceId ? subscribeToFormInstance(formInstanceId, onStoreChange) : _temp;
    $[0] = formInstanceId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const subscribeFn = t0;
  let t1;
  if ($[2] !== formInstanceId) {
    t1 = () => formInstanceId ? getFormFieldErrorsSnapshot(formInstanceId) : EMPTY_FORM_FIELD_ERRORS;
    $[2] = formInstanceId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const getSnapshotFn = t1;
  return useSyncExternalStore(subscribeFn, getSnapshotFn, _temp2);
}
function _temp2() {
  return EMPTY_FORM_FIELD_ERRORS;
}
function _temp() {}
export function focusFirstInvalidField(errors: FormFieldErrors): void {
  if (typeof document === "undefined") {
    return;
  }
  const firstName = Object.keys(errors)[0];
  if (!firstName) {
    return;
  }
  const escaped = typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(firstName) : firstName.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  const el = document.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`[name="${escaped}"]`);
  if (!el) {
    return;
  }
  el.scrollIntoView({
    behavior: "smooth",
    block: "center"
  });
  el.focus({
    preventScroll: true
  });
}